import { FirestoreService } from "../../../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../../api/firebase/references";
import {
  AuthStatusChangeComments,
  isValidAuthStatusChangeComments,
} from "../../../../../data-model/authStatusChangeComments";
import { Comment } from "../../../../../data-model/medicalPaOrdersModel";
import { logError } from "../../../../../utils/customLogger";
import { getSavedAuthStatusChangeCommentsFromLocalStorage } from "../../../../singleOrderLayout/utils/helperForAuthStatusCommentsInLocalStorage";

/**
 * Updates a Firestore document with authorization status change comments retrieved from local storage.
 * Retrieves comments from local storage, validates them, groups them by comment type (denial, query, query_resolution),
 * and writes them to the Firestore document for the specified order.
 *
 * @param orderId - The unique identifier of the order to update
 * @returns A promise that resolves when the update is complete, or returns early if no valid comments exist
 */
export const updateFirestoreDocumentWithComments = async (orderId: string) => {
  const comments = getSavedAuthStatusChangeCommentsFromLocalStorage(orderId);
  if (!comments || !isValidAuthStatusChangeComments(comments ?? [])) {
    return;
  }
  const groupedComments = groupCommentsByCommentType(comments);
  const payload = {
    denial_comments: removeCommentTypeFieldFromComments(groupedComments.denial),
    query_comments: removeCommentTypeFieldFromComments(groupedComments.query),
    query_resolution_comments: removeCommentTypeFieldFromComments(
      groupedComments.query_resolution,
    ),
  };
  await writeToFirestoreDocumentWithComments(orderId, {
    comments: payload,
  });
};

/**
 * Groups authorization status change comments by their comment type.
 * Organizes comments into three categories: denial, query, and query_resolution.
 *
 * @param comments - Array of authorization status change comments to group
 * @returns An object containing arrays of comments grouped by type (denial, query, query_resolution)
 */
const groupCommentsByCommentType = (comments: AuthStatusChangeComments[]) => {
  const initialValue = {
    denial: [] as AuthStatusChangeComments[],
    query: [] as AuthStatusChangeComments[],
    query_resolution: [] as AuthStatusChangeComments[],
  };
  return comments.reduce((acc, comment) => {
    acc[comment.comment_type] = [...acc[comment.comment_type], comment];
    return acc;
  }, initialValue);
};

/**
 * Writes comment data to a Firestore document for a specific order.
 * Updates the medical PA orders collection with the provided comment payload.
 *
 * @param orderId - The unique identifier of the order to update
 * @param payload - The comment data to write to Firestore
 * @throws Error if the Firestore update operation fails
 */
const writeToFirestoreDocumentWithComments = async (
  orderId: string,
  payload: { comments: Comment },
) => {
  try {
    await FirestoreService.updateDocument(
      FirestoreCollectionReference.medicalPaOrders(),
      orderId,
      payload,
    );
  } catch (error) {
    logError(error as Error, "Error updating firestore document with comments");
    throw new Error("Error updating firestore document with comments");
  }
};

/**
 * Removes the comment_type field from an array of authorization status change comments.
 * Creates new comment objects without the comment_type property, keeping all other fields intact.
 *
 * @param comments - Array of authorization status change comments
 * @returns Array of comments with the comment_type field removed
 */
const removeCommentTypeFieldFromComments = (
  comments: AuthStatusChangeComments[],
) => {
  return comments.map((comment) => {
    const { comment_type, ...rest } = comment;
    return rest;
  });
};
